import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/core/utils';

interface InlineTextEditorProps {
  value: string;
  onSave: (newValue: string) => void;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  editableClassName?: string;
  maxLength?: number;
  disabled?: boolean;
  displayValue?: string; // Optional display value (processed) - if not provided, uses value
  variant?: 'dark' | 'light'; // 'dark' for preview (default), 'light' for cards
}

export const InlineTextEditor: React.FC<InlineTextEditorProps> = ({
  value,
  onSave,
  placeholder = "Click to edit...",
  multiline = false,
  className = "",
  editableClassName = "",
  maxLength,
  disabled = false,
  displayValue,
  variant = 'dark'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isHovered, setIsHovered] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  // Update editValue when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Select all text for easy replacement
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (disabled) return;
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue !== value) {
      onSave(trimmedValue);
      setJustSaved(true);
      // Clear the saved indicator after animation
      setTimeout(() => setJustSaved(false), 1000);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (e.key === 'Enter') {
      if (!multiline || (multiline && e.ctrlKey)) {
        e.preventDefault();
        handleSave();
      }
    }
  };

  const handleBlur = () => {
    // Small delay to allow for potential click events
    setTimeout(() => {
      if (isEditing) {
        handleSave();
      }
    }, 100);
  };

  const finalDisplayValue = displayValue || value || placeholder;
  const showPlaceholder = !value && !isEditing;

  if (isEditing) {
    const EditComponent = multiline ? 'textarea' : 'input';

    return (
      <EditComponent
        ref={inputRef as any}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        maxLength={maxLength}
        className={cn(
          "w-full rounded px-2 py-1 text-inherit font-inherit resize-none",
          variant === 'dark' && "bg-white/20 border border-white/30 focus:bg-white/30 focus:border-white/50 focus:ring-white/20",
          variant === 'light' && "bg-muted border border-border text-foreground focus:bg-background focus:border-primary focus:ring-primary/20",
          "focus:outline-none focus:ring-2",
          editableClassName
        )}
        rows={multiline ? 2 : undefined}
        style={{
          fontSize: 'inherit',
          lineHeight: 'inherit',
          fontFamily: 'inherit',
          textAlign: 'inherit',
          minHeight: multiline ? '2.5rem' : 'auto'
        }}
      />
    );
  }

  return (
    <span
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        "cursor-text transition-all duration-200 rounded px-2 py-1 -mx-2 -my-1 relative inline-block",
        variant === 'dark' && !disabled && "hover:bg-white/10 hover:backdrop-blur-sm",
        variant === 'dark' && isHovered && !disabled && "bg-white/5",
        variant === 'light' && !disabled && "hover:bg-muted hover:backdrop-blur-sm",
        variant === 'light' && isHovered && !disabled && "bg-muted/50",
        justSaved && "bg-green-500/20 border border-green-400/50",
        showPlaceholder && (variant === 'dark' ? "text-gray-400 italic" : "text-muted-foreground italic"),
        disabled && "cursor-not-allowed opacity-50",
        className
      )}
      title={disabled ? undefined : "Click to edit"}
    >
      {finalDisplayValue}
      {isHovered && !disabled && !justSaved && (
        <span className={cn(
          "absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-all opacity-100 z-20",
          variant === 'dark' && "bg-black/20 hover:bg-black/30",
          variant === 'light' && "bg-primary/20 hover:bg-primary/30"
        )}>
          <svg className={cn("w-2 h-2", variant === 'dark' ? "text-white" : "text-primary")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </span>
      )}
      {justSaved && (
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500/80 rounded-full flex items-center justify-center transition-all opacity-100 z-20">
          <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        </span>
      )}
    </span>
  );
};

export default InlineTextEditor;